<template>
  <div>
    <form class="relative flex flex-col gap-4 xl:gap-6" @submit.prevent="invokeReturn">
      <slot name="back-button" />
      <div>
        <Heading color="blue" size="large" tag="h1">
          <CommonLabel path="BWF/shop/account/orderHistory/returnHeadline" />
        </Heading>
        <Heading
          class="flex flex-wrap items-center gap-1 gap-x-4 xl:gap-4"
          size="large"
          tag="h2"
          weight="normal"
        >
          <span>
            <CommonLabel path="BWF/shop/account/orderHistory/orderNumber" />
            {{ order?.orderNumber }}
          </span>
          <AccountOrderStateLabel :state="order?.stateMachineState" />
        </Heading>
      </div>

      <template v-if="!isEditMode()">
        <LoadingElement v-if="isLoading" class="bg-white/70" :overlay="true" />
        <template v-if="order">
          <Divider class="hidden xl:block" />

          <OrderSelectableItems v-if="!showReturnSummary" :order="order" />

          <OrderLineItem
            v-for="selectedItem in selectedLineItems"
            v-else
            :key="selectedItem.id"
            :is-configured="true"
            :line-item="selectedItem"
            :quantity-change="false"
          />

          <Divider class="hidden xl:block" />

          <div class="mt-4 flex flex-col">
            <div class="font-medium xl:text-xl">
              <CommonLabel path="BWF/shop/account/orderHistory/returnReasons" />
            </div>
            <SelectField
              v-model="returnFormState.internalComment"
              :error-message="returnForm$?.internalComment?.$errors?.[0]?.$message"
              :label="reasonLabel"
              :options="returnReasons"
              class="form-field-grey xl:w-1/2"
              name="returnReason"
              required="required"
            />
          </div>
        </template>
        <Divider />
      </template>

      <div class="flex flex-col items-start gap-4 xl:flex-row">
        <div class="w-full xl:w-8/12">
          <CommonLabel path="BWF/shop/shared/mandatoryFieldsLabel" />

          <EditableArea :content="hintBox" :custom-view="EmptyArea" />
          <div v-if="hintBox?.showHintBox" class="rounded bg-light-grey p-4">
            <div class="font-medium">
              {{ hintBox.headline }}
            </div>
            <div class="prose" v-html="hintBox?.richText" />
            <ContactElement v-if="hintBox.contact" :contact="hintBox.contact" class="mt-4" />
          </div>
        </div>
        <button :disabled="buttonDisabled" class="btn btn-blue w-full xl:ml-auto xl:w-4/12">
          <CommonLabel
            v-if="showReturnSummary"
            path="BWF/shop/account/orderHistory/confirmReturn"
          />
          <CommonLabel v-else path="BWF/shop/shared/continue" />
        </button>
      </div>
    </form>
  </div>

  <ModalComponent :controller="contractCancellationAcknowledgementModal">
    <template #headline>
      <Paragraph class="text-center">
        <CommonLabel path="BWF/shop/account/subscriptions/modal/confirmReturnHeadline" />
      </Paragraph>
    </template>
    <template #richText>
      <CommonLabel path="BWF/shop/account/subscriptions/modal/confirmReturnDescription" />
    </template>

    <div class="mx-auto grid w-full gap-5 md:grid-cols-2">
      <button class="btn btn-blue" @click="contractCancellation()">
        <CommonLabel path="BWF/shop/account/orderDetails/modal/confirmCancellation" />
      </button>
      <button class="btn btn-ghost" @click="contractCancellationAcknowledgementModal.close()">
        <CommonLabel path="BWF/shop/account/orderDetails/modal/declineCancellation" />
      </button>
    </div>
  </ModalComponent>
</template>

<script setup>
import { EditableArea } from "@magnolia/vue-editor";
import { useShopwareContext } from "@shopware-pwa/composables-next";
import { useVuelidate } from "@vuelidate/core";
import { Divider } from "~/components/clarity-ui/Divider";
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import useCommonLabels from "~/composables/useCommonLabels";
import { useModal } from "~/composables/useModal";
import EmptyArea from "~/templates/areas/EmptyArea.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import OrderSelectableItems from "~/templates/components/shop/account/OrderSelectableItems.vue";
import SelectField from "~/templates/elements/form/SelectField.vue";
import ContactElement from "~/templates/elements/ContactElement.vue";
import LoadingElement from "~/templates/elements/LoadingElement.vue";
import { useOrderSelection } from "~/composables/shop/useOrderSelection";
import ModalComponent from "~/templates/components/ModalComponent.vue";
import OrderLineItem from "~/templates/components/shop/account/OrderLineItem.vue";
import { isEditMode } from "~/utils/content/magnolia";
import { containsReturnRestricted, hasContractPlan } from "~/utils/helper/shop/order";

const props = defineProps([
  "showHintBox",
  "hintBox",
  "confirmModalArea",
  "serviceModalArea",
  "bottom",
  "isLoading",
]);

const { getLabelByPath, getLabels } = useCommonLabels();

const { pushError } = useNotifications();

const emit = defineEmits(["confirm-modal", "service-modal", "end-loading"]);

const { order } = useOrderDetails();
const { apiInstance } = useShopwareContext();
const reasonLabel = getLabelByPath("BWF/shop/account/orderHistory/reasonLabel");
const { selectedItems, selectedLineItems } = useOrderSelection(order);

const contractCancellationAcknowledgementModal = useModal();
const showReturnSummary = ref(false);

const contractCancellation = () => {
  showReturnSummary.value = true;
  contractCancellationAcknowledgementModal.close();
};

const { returnOf, failed } = getLabels("BWF/shop/account/orderDetails/", ["returnOf", "failed"]);

const { requiredValidator, oneItemRequired } = await useValidation();

const returnFormState = reactive({
  lineItems: selectedItems,
  internalComment: "",
});
const formIsLocked = ref(false);
const buttonDisabled = computed(() => {
  return props.isLoading || formIsLocked.value;
});

const returnFormRules = computed(() => ({
  internalComment: {
    ...requiredValidator,
  },
  lineItems: {
    ...oneItemRequired,
  },
}));
const returnForm$ = useVuelidate(returnFormRules, returnFormState);

const selectedHasContracts = computed(() => hasContractPlan(selectedLineItems?.value));
const invokeReturn = async () => {
  if (selectedHasContracts.value && !showReturnSummary.value) {
    contractCancellationAcknowledgementModal.open();
  } else {
    const valid = await returnForm$.value.$validate();
    if (valid) {
      if (containsReturnRestricted(selectedLineItems.value)) {
        emit("service-modal", "end-loading");
        return;
      }

      try {
        formIsLocked.value = true;
        const {
          data: {
            translated: {},
          },
          error,
        } = await apiInstance.invoke.post(`/store-api/order/${order?.value?.id}/partial/return`, {
          ...returnFormState,
        });
        if (error?.value) {
          pushError(error?.value?.messages?.[0]?.detail);
        }
        formIsLocked.value = false;
        emit("confirm-modal");
      } catch (e) {
        if (e?.messages?.[0]?.detail) {
          pushError(e?.messages?.[0]?.detail);
        } else {
          pushError(`${returnOf} (${order?.value?.orderNumber}) ${failed}`);
        }
      }
    } else if (returnForm$?.value?.lineItems?.$errors?.[0]?.$message) {
      pushError(returnForm$?.value?.lineItems?.$errors?.[0]?.$message);
    }
  }
};

const { data: shopReturnReasons } = await useAsyncData(
  "swReturnReasons",
  async () => {
    const {
      data: { elements },
    } = await apiInstance.invoke.get("/store-api/web/order/return/reason");
    return elements;
  },
  { lazy: true },
);

const returnReasons = computed(() => {
  return (
    shopReturnReasons?.value?.map((reason) => ({
      label: reason?.name ?? "",
      value: reason?.id,
    })) ?? []
  );
});
</script>
